import React from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { queryClient } from './queries/queryClient';
import { AuthProvider } from './contexts/AuthContext';
import { SessionProvider } from './contexts/SessionContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { NotificationProvider } from './components/NotificationSystem';
import { ErrorBoundary } from './components/shared';
import ThemeInitializer from './components/ThemeInitializer';
import ProtectedRoute from './components/ProtectedRoute';
import RootRedirect from './components/RootRedirect';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import AcceptInvite from './components/AcceptInvite';
import AuthCallback from './components/AuthCallback';
// Plan 167 / #441: ProjectDetails, AnalysisPage, ResultsPage,
// ComparisonResultsPage, HistoryPage deleted — findings now overlay clause
// cards in the Desk. Stale /project/:id bookmarks redirect to /desk.
import ClauseView from './pages/ClauseView';
import HelpPage from './pages/HelpPage';
import HelpSupportPage from './pages/HelpSupportPage';
import PromptManagement from './pages/PromptManagement';
import ChatPage from './pages/ChatPage';
import { PipelineProgressIndicatorDemo } from './components/pipeline';
import { DocumentWorkspace } from './prototype/DocumentWorkspace';
import DeskPage from './pages/DeskPage';
import PlaybookBuilder from './components/playbook/PlaybookBuilder';
import WorkbenchBuilder from './components/workbench/WorkbenchBuilder';
import AdminRoute from './components/shared/AdminRoute';
import AdminMetricsPage from './pages/AdminMetricsPage';
import AdminFeedbackPage from './pages/AdminFeedbackPage';
import TermsGate from './components/pilot/TermsGate';  // Plan 206
import './styles/index.css';

const App: React.FC = () => {
  return (
    <ErrorBoundary level="app" onError={(error, errorInfo) => {
      // In production, you could send this to an error reporting service
      console.error('App-level error:', { error, errorInfo });
    }}>
      <ThemeInitializer />
      <NotificationProvider>
        <AuthProvider>
          <OnboardingProvider>
            <SessionProvider>
            <QueryClientProvider client={queryClient}>
            <Router>
            <TermsGate>
            <div className="App">
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={
                  <ErrorBoundary level="page">
                    <Login />
                  </ErrorBoundary>
                } />
                <Route path="/register" element={
                  <ErrorBoundary level="page">
                    <Register />
                  </ErrorBoundary>
                } />
                <Route path="/forgot-password" element={
                  <ErrorBoundary level="page">
                    <ForgotPassword />
                  </ErrorBoundary>
                } />
                <Route path="/reset-password" element={
                  <ErrorBoundary level="page">
                    <ResetPassword />
                  </ErrorBoundary>
                } />
                <Route path="/accept-invite" element={
                  <ErrorBoundary level="page">
                    <AcceptInvite />
                  </ErrorBoundary>
                } />
                <Route path="/auth/callback" element={
                  <ErrorBoundary level="page">
                    <AuthCallback />
                  </ErrorBoundary>
                } />

                {/* Prototype Routes (no auth required for design review) */}
                <Route path="/prototype/workspace" element={
                  <DocumentWorkspace />
                } />

                {/* Protected Routes */}
                {/* Legacy /dashboard redirect for bookmarks */}
                <Route path="/dashboard" element={<Navigate to="/desk" replace />} />

                {/* Plan 167 / #441: ProjectDetails deleted. Verified no
                    Desk or backend code generates /project/:id links;
                    stale browser bookmarks redirect to /desk so they
                    land somewhere useful instead of 404'ing. */}
                <Route path="/project/:id" element={<Navigate to="/desk" replace />} />

                {/* Plan 167 / #441: legacy analysis / results /
                    comparison-results routes deleted. Anything navigating
                    to these paths now falls through to the catch-all. */}

                {/* Clause Deep-Linking Routes - Task 50.1 */}
                <Route path="/documents/:documentId/clause/:clauseId" element={
                  <ProtectedRoute>
                    <ErrorBoundary level="page">
                      <ClauseView />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                
                <Route path="/project/:projectId/documents/:documentId/clause/:clauseId" element={
                  <ProtectedRoute>
                    <ErrorBoundary level="page">
                      <ClauseView />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                
                <Route path="/help" element={
                  <ProtectedRoute>
                    <ErrorBoundary level="page">
                      <HelpPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                
                {/* Plan 167 / #441: /history route + HistoryPage deleted. */}

{/* Task 94: Prompt Management Route */}
                <Route path="/prompts" element={
                  <ProtectedRoute>
                    <ErrorBoundary level="page">
                      <PromptManagement />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />

                {/* Task 111: Chat Interface Route */}
                <Route path="/chat/:sessionId?" element={
                  <ProtectedRoute>
                    <ErrorBoundary level="page">
                      <ChatPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />

                {/* Task 60 Demo Route */}
                <Route path="/demo/progress" element={
                  <ProtectedRoute>
                    <ErrorBoundary level="page">
                      <PipelineProgressIndicatorDemo />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />

                {/* Plan 97: Playbook Builder Routes */}
                <Route path="/playbook-builder" element={
                  <ProtectedRoute>
                    <ErrorBoundary level="page">
                      <PlaybookBuilder />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/playbook-builder/:id" element={
                  <ProtectedRoute>
                    <ErrorBoundary level="page">
                      <PlaybookBuilder />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />

                {/* Plan 152: Workbench Precedent Library */}
                <Route path="/workbench" element={
                  <ProtectedRoute>
                    <ErrorBoundary level="page">
                      <WorkbenchBuilder />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/workbench/:id" element={
                  <ProtectedRoute>
                    <ErrorBoundary level="page">
                      <WorkbenchBuilder />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />

                {/* Plan 101: Admin Quality Metrics Dashboard */}
                <Route path="/admin/metrics" element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <ErrorBoundary level="page">
                        <AdminMetricsPage />
                      </ErrorBoundary>
                    </AdminRoute>
                  </ProtectedRoute>
                } />

                <Route path="/admin/feedback" element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <ErrorBoundary level="page">
                        <AdminFeedbackPage />
                      </ErrorBoundary>
                    </AdminRoute>
                  </ProtectedRoute>
                } />

                {/* Plan 121: Help & Support Page */}
                <Route path="/desk/help" element={
                  <ProtectedRoute>
                    <ErrorBoundary level="page">
                      <HelpSupportPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />

                {/* Task 198: Desk Workspace Routes (Phase 10) */}
                <Route path="/desk" element={
                  <ProtectedRoute>
                    <ErrorBoundary level="page">
                      <DeskPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/desk/:matterId" element={
                  <ProtectedRoute>
                    <ErrorBoundary level="page">
                      <DeskPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />

                {/* / is the single post-auth landing entry. RootRedirect
                    sends everyone to /desk; wrapped in ProtectedRoute so
                    unauthenticated visits bounce to /login first. */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <RootRedirect />
                  </ProtectedRoute>
                } />

                {/* Catch all - redirect to Desk. Unknown paths should NOT
                    divert users to chat; if they typed a bad URL they likely
                    meant a workspace destination. */}
                <Route path="*" element={<Navigate to="/desk" replace />} />
              </Routes>
            </div>
            </TermsGate>
            </Router>
            {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
            </QueryClientProvider>
            </SessionProvider>
          </OnboardingProvider>
        </AuthProvider>
      </NotificationProvider>
    </ErrorBoundary>
  );
};

export default App;